import { WearablesEntity } from "./wearables.entities";

export function calculateRowMetrics(group: WearablesEntity[]) {

    const totalParticipants = group.length;
    const baseValues = getSyncedCountValues(group, 'SyncedBaseDaysValue');
    const moiValues = getSyncedCountValues(group, 'SyncedMOIDaysValue');
    const eoiValues = getSyncedCountValues(group, 'SyncedEOIDaysValue');
    const eosValues = getSyncedCountValues(group, 'SyncedEOSDaysValue');
    const num3day = group.reduce((total, val) => total + val.NumDay3Nudge, 0);
    const num7day = group.reduce((total, val) => total + val.NumDay7Nudge, 0);
    const num10day = group.reduce((total, val) => total + val.NumDay10Nudge, 0);
    const notifyCoord = group.reduce((total, val) => total + val.TotalNotifyCoord, 0);

    return {
        totalParticipants,
        syncedBase: getPercentSyncedCell(baseValues.sevenCount, baseValues.totalCount, emptyRange),
        avgSyncedBase: getAvgSyncedCell(baseValues),
        zeroSyncedBase: getPercentSyncedCell(baseValues.zeroCount, baseValues.totalCount, zeroRange),
        syncedMoi: getPercentSyncedCell(moiValues.sevenCount, moiValues.totalCount, emptyRange),
        avgSyncedMoi: getAvgSyncedCell(moiValues),
        zeroSyncedMoi: getPercentSyncedCell(moiValues.zeroCount, moiValues.totalCount, zeroRange),
        syncedEoi: getPercentSyncedCell(eoiValues.sevenCount, eoiValues.totalCount, emptyRange),
        avgSyncedEoi: getAvgSyncedCell(eoiValues),
        zeroSyncedEoi: getPercentSyncedCell(eoiValues.zeroCount, eoiValues.totalCount, zeroRange),
        syncedEos: getPercentSyncedCell(eosValues.sevenCount, eosValues.totalCount, emptyRange),
        avgSyncedEos: getAvgSyncedCell(eosValues),
        zeroSyncedEos: getPercentSyncedCell(eosValues.zeroCount, eosValues.totalCount, zeroRange),
        num3day,
        num7day,
        num10day,
        notifyCoord,
    }
}

function getSyncedCountValues(group: WearablesEntity[], prop) {
    return group.reduce((t, v) => {
        if (Number.isNaN(v[prop])) return t;
        return {
            sevenCount: (v[prop] === 7) ? t.sevenCount + 1 : t.sevenCount,
            zeroCount: (v[prop] === 0) ? t.zeroCount + 1 : t.zeroCount,
            totalCount: t.totalCount + 1,
            sum: t.sum = t.sum + v[prop],
        }
    }, {sevenCount: 0, zeroCount: 0, totalCount: 0, sum: 0,});
}

function getPercentSyncedCell(value, total, highlightRange) {
    const percent = Math.round((value / total) * 100);
    const percentDisplay = (total > 0) ? `${percent}%` : '&nbsp;';
    const totalDisplay = (total > 0) ? `${value} / ${total}` : '&nbsp;';
    return getFormattedCellString(percentDisplay, percent, totalDisplay, getHilightClass(percent, highlightRange));
}

function getAvgSyncedCell(value) {
    const avg = (value.sum / value.totalCount);
    const avgDisplay = (value.totalCount > 0) ? `${avg.toFixed(1)}` : '&nbsp;';
    return getFormattedCellString(avgDisplay, avg, '&nbsp;', getHilightClass(avg,avgRange));
}

function getFormattedCellString(percentDisplay: string, percentValue: number, totalDisplay = '', highlightClass = '') {
    return {
        display: `<div class="sync-metric ${highlightClass}"><div class="sync-percent ${highlightClass}">${percentDisplay}</div><div class="sync-total ${highlightClass}">${totalDisplay}</div></div>`,
        value: Number.isNaN(percentValue) ? -1 : percentValue, //using -1 to sort below a zero value
    };
}

function getHilightClass(val, range=emptyRange) {
    if (Number.isNaN(val)) return '';
    else if (val >= range.high) return range.highClass;
    else if (val >= range.mid) return range.midClass;
    else return range.lowClass;
}

export function calculateParticipantRowMetrics(participant: WearablesEntity) {
    return {
        platform: participant.StudyDisplay,
        trial: participant.AppendixDisplay,
        siteId: participant.SiteNumber,
        participantId: participant.SubjectId,
        syncedBase: participant.CountSyncedBase,
        baseTargetDate: participant.BaseTargetDate,
        syncedMoi: participant.CountSyncedMOI,
        moiTargetDate: participant.MOITargetDate,
        syncedEoi: participant.CountSyncedEOI,
        eoiTargetDate: participant.EOITargetDate,
        syncedEos: participant.CountSyncedEOS,
        eosTargetDate: participant.EOSTargetDate,
        num3day: participant.NumDay3Nudge,
        num7day: participant.NumDay7Nudge,
        num10day: participant.NumDay10Nudge,
        notifyCoord: participant.TotalNotifyCoord,
    };
}

const emptyRange ={
    high: 0,
    highClass:'',
    mid: 0,
    midClass:'',
    lowClass: ''
};

const sevenRange ={
    high: 70,
    highClass:'sync-green',
    mid: 35,
    midClass:'sync-yellow',
    lowClass: 'sync-red'
};

const zeroRange ={
    high: 50,
    highClass:'sync-red',
    mid: 10,
    midClass:'sync-yellow',
    lowClass: 'sync-green'
};

const avgRange ={
    high: 5,
    highClass:'sync-green',
    mid: 3,
    midClass:'sync-yellow',
    lowClass: 'sync-red'
};

export const EmptyMetric = {display: '', value: -2};

export const metricColumnDefs = [
    {
        data:'syncedBase',
        title: '<div title="Synced All 7 Days Prior to Baseline"> <br>Synced 7 Days</div>',
        className: 'sync-col text-center font-size12 nowrap baseline-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'avgSyncedBase',
        title: '<div title="Average number of days synced prior to Baseline">Baseline<br>Avg. Days</div>',
        className: 'sync-col text-center font-size12 nowrap baseline-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'zeroSyncedBase',
        title: '<div title="Synced 0 Days Prior to Baseline"> <br>Synced 0 Days</div>',
        className: 'sync-col text-center font-size12 nowrap baseline-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'syncedMoi',
        title: '<div title="Synced All 7 Days Prior to MOI"><br>Synced 7 Days</div>',
        className: 'sync-col text-center font-size12 nowrap moi-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'avgSyncedMoi',
        title: '<div title="Average number of days synced prior to MOI">MOI<br>Avg. Days</div>',
        className: 'sync-col text-center font-size12 nowrap moi-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'zeroSyncedMoi',
        title: '<div title="Synced 0 Days Prior to MOI"><br>Synced 0 Days</div>',
        className: 'sync-col text-center font-size12 nowrap moi-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'syncedEoi',
        title: '<div title="Synced All 7 Days Prior to EOI"><br>Synced 7 Days</div>',
        className: 'sync-col text-center font-size12 nowrap eoi-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'avgSyncedEoi',
        title: '<div title="Average number of days synced prior to EOI">EOI<br>Avg. Days</div>',
        className: 'sync-col text-center font-size12 nowrap eoi-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'zeroSyncedEoi',
        title: '<div title="Synced 0 Days Prior to EOI"><br>Synced 0 Days</div>',
        className: 'sync-col text-center font-size12 nowrap eoi-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'syncedEos',
        title: '<div title="Synced All 7 Days Prior to EOS"><br>Synced 7 Days</div>',
        className: 'sync-col text-center font-size12 nowrap eos-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'avgSyncedEos',
        title: '<div title="Average number of days synced prior to ESO">EOS<br>Avg. Days</div>',
        className: 'sync-col text-center font-size12 nowrap eos-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'zeroSyncedEos',
        title: '<div title="Synced 0 Days Prior to EOS"><br>Synced 0 Days</div>',
        className: 'sync-col text-center font-size12 nowrap eos-col',
        render: {
            display: 'display',
            _: 'value'
        }
    },
    {
        data:'num3day',
        title: '<br>Day 3',
        className: 'text-center font-size12 nowrap nudge-col',
    },
    {
        data:'num7day',
        title: 'Nudges<br>Day 7',
        className: 'text-center font-size12 nowrap nudge-col',
    },
    {
        data:'num10day',
        title: '<br>Day 10',
        className: 'text-center font-size12 nowrap nudge-col',
    },
    {
        data:'notifyCoord',
        title: 'Coordinator<br>Notifications',
        className: 'text-center font-size12 nowrap',
    },
];

export const dialogMetricColumnDefs = [
    { data:'syncedBase', title: 'Days Synced<br>Prior to Baseline', className: 'text-center font-size12 nowrap multiline-col' },
    { data:'baseTargetDate', title: 'Baseline<br>Target Date', className: 'text-center font-size12 nowrap multiline-col', type: 'date' },
    { data:'syncedMoi', title: 'Days Synced<br>Prior to MOI', className: 'text-center font-size12 nowrap multiline-col' },
    { data:'moiTargetDate', title: 'MOI<br>Target Date', className: 'text-center font-size12 nowrap multiline-col', type: 'date' },
    { data:'syncedEoi', title: 'Days Synced<br>Prior to EOI', className: 'text-center font-size12 nowrap multiline-col' },
    { data:'eoiTargetDate', title: 'EOI<br>Target Date', className: 'text-center font-size12 nowrap multiline-col', type: 'date' },
    { data:'syncedEos', title: 'Days Synced<br>Prior to EOS', className: 'text-center font-size12 nowrap multiline-col' },
    { data:'eosTargetDate', title: 'EOS<br>Target Date', className: 'text-center font-size12 nowrap multiline-col', type: 'date' },
    { data:'num3day', title: 'Day 3<br>Nudges', className: 'text-center font-size12 nowrap' },
    { data:'num7day', title: 'Day 7<br>Nudges', className: 'text-center font-size12 nowrap' },
    { data:'num10day', title: 'Day 10<br>Nudges', className: 'text-center font-size12 nowrap' },
    { data:'notifyCoord', title: 'Coordinator<br>Notifications', className: 'text-center font-size12 nowrap' },
];
